import { Injectable, NgZone, inject } from '@angular/core';
import { Router } from '@angular/router';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { environment } from '@environments/environment';
import { paths } from '@platform/paths';
import { KeyboardService } from './keyboard.service';
import { NavigationService } from './navigation.service';

declare const cordova;

@Injectable({
  providedIn: 'root',
})
export class MobileAppService {
  private readonly navigationService = inject(NavigationService);
  private readonly keyboardService = inject(KeyboardService);
  private readonly zone = inject(NgZone);
  private readonly router = inject(Router);

  private readonly isMobileApp = environment.mobileApp?.isMobileApp ?? false;
  private readonly isAndroid = Capacitor.getPlatform() === 'android';

  applyAppFeatures(): void {
    if (!this.isMobileApp) return;

    this.handleAndroidBackButton();
    this.handleDeepLinks();
    this.handleStatusBar();
    this.keyboardService.registerKeyboardListeners();
    if (this.isAndroid) this.initializeAndroidSingular();
  }

  handleAndroidBackButton(): void {
    App.addListener('backButton', () => {
      this.navigationService.back();
    });
  }

  handleStatusBar(): void {
    StatusBar.setStyle({ style: Style.Light });
    StatusBar.setBackgroundColor({ color: '#ffffff' });

    if (this.isAndroid) document.body.classList.add('mobile-safe-area-android-padding');
  }

  handleDeepLinks(): void {
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.zone.run(() => {
        const url = new URL(event.url);
        const deferred = url?.searchParams.get('deferred') === 'true';
        if (deferred) this.handleIosDeferred(url);
      });
    });
  }

  handleIosDeferred(url: URL): void {
    const referralCode = url.searchParams.get('passthrough') || environment.mobileApp?.referralCode;
    this.router.navigate([paths.SIGN_UP], { queryParams: { 'referral-code': referralCode } });
  }

  initializeAndroidSingular(): void {
    try {
      document.addEventListener('deviceready', () => {
        if (!cordova?.plugins?.SingularCordovaSdk) return;
        const singularConfig = new cordova.plugins.SingularCordovaSdk.SingularConfig(
          environment.mobileApp?.singularConfig.key,
          environment.mobileApp?.singularConfig.secret,
        );
        const router = this.router;
        const linkHandler = function (data): void {
          const isDeferred = data.isDeferred;
          const referralCode = data.urlParameters?.pcid || environment.mobileApp?.referralCode;
          if (isDeferred) router.navigate([paths.SIGN_UP], { queryParams: { 'referral-code': referralCode } });
        };

        singularConfig.withSingularLink(linkHandler);
        singularConfig.withLoggingEnabled();
        cordova.plugins.SingularCordovaSdk.init(singularConfig);
      });
    } catch (error) {
      console.info('Error init singular', error);
    }
  }
}
